import { Utils } from '../Utils';
import { UwFlatExtra } from '../../../common/js/interfaces/UwFlatExtra';
import { BenefitType, Product, RiderType } from '../static/Product';
import { ProductApiTypes } from '@hit/policy-admin-types';
import { FlatExtra } from '../customer/FlatExtra';
import { Policy } from '../customer/Policy';
import { CommonUtils } from '../static/CommonUtils';
import { POLICY_UW_TYPES } from '../constants/policy';

export interface PremiumRequest {
  product: string;
  productType: string;
  state: string;
  gender: '1' | '2';
  ratingCode: string;
  dob: Date;
  effectiveDate: Date;
  term: number;
  face: number;
  waiver: boolean;
  flatExtras: Array<UwFlatExtra>;
  paymentFrequency: string;
  riders?: Array<Rider>;
  asOfDate?: Date;
}
export interface PremiumSolveRequest {
  product: string;
  productType: string;
  state: string;
  gender: '1' | '2';
  ratingCode: string;
  dob: Date;
  effectiveDate: Date;
  term: number;
  face?: number;
  waiver: boolean;
  flatExtras: Array<UwFlatExtra>;
  paymentFrequency: string;
  riders?: Array<Rider>;
  asOfDate?: Date;
  premium: number;
}

export interface Rider {
  type: string;
  amount: number;
  premium?: number;
  waiver?: number;
  flatExtras?: Array<FlatExtra>;
  plannedYears?: number;
  paymentFrequency?: string;
  accelerationPercent?: number;
  monthlyIncreaseOption?: boolean;
  puaAddedToBenefitPool?: boolean;
  predictedBenefitAge?: number;
}
export interface PremiumRequestList {
  requests: Array<PremiumRequest>;
}

export interface PremiumResponse {
  version: string;
  base: number;
  waiver: number;
  riders?: Array<Rider>;
  flatExtras?: Array<FlatExtraResponse>;
  annualBaseRates?: Array<number>;
  annualWaiverRates?: Array<number>;
  totalTargetPremium: number;
  annualBase: number;
}

export interface ProductSpecRequest {
  insuredAge: number;
  product: string;
  productType: string;
  duration: number;
  state: string;
  referenceId?: string;
}

export interface ProductSpecResponse {
  convertibleAllow: string;
  convertibleEndAge: number;
  convertibleMinYears: number;
  convertibleMaxYears: number;
  fixedLoanInterestRate: number;
  variableLoanInterestRate: number;
}

export interface PremiumResponseList {
  responses: Array<PremiumResponse>;
}

export interface FlatExtraResponse {
  name: string;
  premium: number;
  ruleCode?: string;
  amount?: number;
  expiryAfterInYears?: number;
}

enum PaymentFrequency {
  Annual = 'Annual',
  Monthly = 'Monthly',
}

export interface PayloadSegmentInfo {
  asOfDate: Date;
  rateClass: string;
  amount: number;
  riders?: Array<Rider>;
  hasWaiver?: boolean;
  flatExtras?;
  includeAdbr?: boolean;
}
export interface SequenceQueryRequest {
  query: string;
  variables: ProductApiTypes.QuerysequenceArgs;
}
export interface SequenceOptions {
  productCode: string;
  referenceId: string;
}

export class ProductServiceUtils {
  static convertPolicyToProductServiceRequestList(
    policy: Policy,
    additionalRequests?: Array<string>
  ): PremiumRequestList {
    const premiumRequestList: PremiumRequestList = {
      requests: [],
    };

    const product = policy.product;
    const productType = policy.productType;
    const gender = policy.insured.person.gender;
    const currentRateClassCode = this.getRateClassCodeForProductRequest(policy);
    const insuredDOB = policy.insured.person.dob;
    const effectiveDate = policy.effectiveDate;
    const insuredState = policy.insured.person.residentialAddress.state;
    const term = policy.term;
    const faceAmount = policy.faceAmount;
    const hasWaiver = policy.waiver;
    const paymentFrequency = policy.paymentFrequency;
    const riders = policy.riders
      .map((rider) => ({
        type: rider.type,
        amount: rider.amount,
        accelerationPercent: rider.benefitDisbursementMethod,
        monthlyIncreaseOption: rider.monthlyIncreaseOption,
      }))
      .filter((rider) => {
        if (rider.type === RiderType.ALIR) {
          return false;
        }
        if (rider.type === RiderType.CIR && (!Utils.exists(rider.amount) || !Utils.exists(rider.accelerationPercent))) {
          return false;
        }
        return Utils.exists(rider.amount);
      });

    const regularPremiumRequest: PremiumRequest = {
      product: product,
      productType: productType,
      gender: gender,
      ratingCode: currentRateClassCode,
      dob: insuredDOB,
      effectiveDate: effectiveDate || Utils.now(),
      state: insuredState,
      term: term,
      face: faceAmount,
      flatExtras: [],
      waiver: hasWaiver,
      paymentFrequency: paymentFrequency,
      riders: riders,
      asOfDate: Utils.now(),
    };

    premiumRequestList.requests.push(regularPremiumRequest);

    if (additionalRequests && additionalRequests.includes('oppositePaymentFrequency')) {
      // if selected paymentFrequency = "Annual", this request is for the "Monthly" premium
      // if selected paymentFrequency = "Monthly", this request is for the "Annual" premium
      let oppositePaymentFrequencyVal = paymentFrequency;
      if (CommonUtils.makePaymentFrequencySafeForCoverpathQuote(oppositePaymentFrequencyVal) === 'Annual') {
        oppositePaymentFrequencyVal = 'Monthly';
      } else {
        oppositePaymentFrequencyVal = 'Annual';
      }

      const oppositePaymentFrequencyRequest: PremiumRequest = Object.assign({}, regularPremiumRequest);
      oppositePaymentFrequencyRequest.paymentFrequency = oppositePaymentFrequencyVal;
      oppositePaymentFrequencyRequest.riders = null;

      premiumRequestList.requests.push(oppositePaymentFrequencyRequest);
    }

    let newProduct = product;
    if (product === Product.products.CASTerm.code || product === Product.products.MMDirectTerm.code) {
      newProduct = product + 'ECP';
    } else if (product === Product.products.CASTermECP.code || product === Product.products.MMDirectTermECP.code) {
      newProduct = product.substring(0, product.length - 3);
    }

    if (additionalRequests && additionalRequests.includes('oppositeECPPremium')) {
      const oppositeECPPremiumRequest: PremiumRequest = Object.assign({}, regularPremiumRequest);
      oppositeECPPremiumRequest.product = newProduct;
      premiumRequestList.requests.push(oppositeECPPremiumRequest);
    }

    return premiumRequestList;
  }

  public static getRateClassCodeForProductRequest(policy: Policy): string {
    let rateClassCode;
    if (
      policy.isAccelerated() ||
      policy.finalUWType === POLICY_UW_TYPES.Algo.code ||
      policy.finalUWType === POLICY_UW_TYPES.Manual.code
    ) {
      rateClassCode = policy.finalRateClass;
    } else if (policy.isTLIC()) {
      rateClassCode = policy.tLICRateClass;
    } else if (policy.application.quote) {
      const isSecondary = policy.isSecondary;
      const healthClass = isSecondary
        ? policy.application.quote.secondaryQuote.healthClass
        : policy.application.quote.healthClass;
      const faceAmount = policy.faceAmount;
      const product = policy.product;
      const productType = policy.productType;
      const faceAndRtrAmount = policy.hasRtrRider ? faceAmount + policy.rtrRiderAmount : faceAmount;
      // Determine if we can use the quote rate class or need to swap in standard/standard tobacco based on face amount
      rateClassCode = CommonUtils.determineRatingClass(healthClass, faceAndRtrAmount, product, productType).code;
    }

    return rateClassCode ? rateClassCode : policy.getCurrentRateClass();
  }
}
